import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback } from 'react';

import { useRecordingAnalytics } from '../../../hooks';
import { selectCaptionSettings } from '../../../selector';

type CaptionPosition = 'top' | 'center' | 'bottom';

const PositionIcon = ({
  position,
  isActive,
}: {
  position: CaptionPosition;
  isActive: boolean;
}) => (
  <div className="flex h-[22px] w-[25px] items-center justify-center">
    <div className="flex flex-col gap-[3px] items-start justify-center rounded-[2px]">
      <div
        className={cx('w-[11px] rounded-[1px]', {
          'h-[3px]': position === 'top',
          'h-[2px]': position !== 'top',
          'bg-spring-neutral-b2': position === 'top' && isActive,
          'bg-spring-neutral-b4': !(position === 'top' && isActive),
        })}
      />
      <div
        className={cx('w-[11px] rounded-[1px]', {
          'h-[3px]': position === 'center',
          'h-[2px]': position !== 'center',
          'bg-spring-neutral-b2': position === 'center' && isActive,
          'bg-spring-neutral-b4': !(position === 'center' && isActive),
        })}
      />
      <div
        className={cx('w-[11px] rounded-[1px]', {
          'h-[3px]': position === 'bottom',
          'h-[2px]': position !== 'bottom',
          'bg-spring-neutral-b2': position === 'bottom' && isActive,
          'bg-spring-neutral-b4': !(position === 'bottom' && isActive),
        })}
      />
    </div>
  </div>
);

const positionToAnalytics = (p: CaptionPosition): 'top' | 'middle' | 'bottom' =>
  p === 'center' ? 'middle' : p;

type CaptionPositionControlsProps = {
  disabled?: boolean;
};

export const CaptionPositionControls = ({
  disabled = false,
}: CaptionPositionControlsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { captions_position } = useAppSelector(selectCaptionSettings);

  const handlePositionChange = useCallback(
    (newPosition: CaptionPosition) => {
      dispatch({
        type: '[recording] captions position changed',
        payload: {
          position: newPosition,
        },
      });
      trackRecordingEvent('ch_creator_lab_appearance_position_selected', {
        position: positionToAnalytics(newPosition),
      });
    },
    [dispatch, trackRecordingEvent],
  );

  const positions: { id: CaptionPosition; labelKey: string }[] = [
    {
      id: 'top',
      labelKey: 'content-recording.appearance.captions.position.top',
    },
    {
      id: 'center',
      labelKey: 'content-recording.appearance.captions.position.center',
    },
    {
      id: 'bottom',
      labelKey: 'content-recording.appearance.captions.position.bottom',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] font-medium text-white leading-[20px] tracking-[0px] m-0">
        {t('content-recording.appearance.captions.position.label')}
      </p>
      <div className="flex gap-3">
        {positions.map(({ id, labelKey }) => (
          <button
            key={id}
            type="button"
            onClick={() => handlePositionChange(id)}
            disabled={disabled}
            className={cx(
              'flex flex-1 flex-col items-center rounded-[8px] py-1 transition-colors',
              {
                'bg-spring-neutral-b5': captions_position === id,
              },
            )}
          >
            <PositionIcon position={id} isActive={captions_position === id} />
            <div className="flex h-[26px] w-full flex-col items-center justify-center">
              <p className="text-[14px] font-semibold leading-[21px] text-white m-0">
                {t(labelKey)}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
